import React, { useCallback, useMemo } from 'react';

import { AppState } from '@excalidraw/excalidraw/types/types';
import { styled, Box, Chip, Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useLocalStorage from '../../../../hooks/useLocalStorage';
import { Page } from '../../types';
import ElementTools from './ElementTools';
import MiscTools from './MiscTools';
import ZoomTools from './ZoomTools';
import { useUserAgent } from '../../../../hooks/useUserAgent';

const StyledBox = styled(Box)` 
    position: absolute;
    margin-top: 0.25rem; 
    padding-left: 0.25rem; 
    padding-right: 0.25rem; 
    z-index: 9; 
    width: 100%;
`;

const StyledChip = styled(Chip)`
    position: absolute;
    bottom: -2rem;
    left: calc(50% + 12px);
    transform: translate(-50%, 0);
    background-color: hsla(0,0%,100%,0.88);
`;

type Props = {
    excalidrawZoom: number;
    activeTool: AppState['activeTool'];
    onDeleteAllConfirm: () => void;
    onDownload: () => void;
    onToolTypeChange: (newToolType: string) => void;
    onZoomChange: (newZoom: number) => void;
    page: Page;
    toolProperties: any
};

const Tools = React.memo((props: Props) => {
    const { t } = useTranslation('whiteboard');

    const [showHelp, setShowHelp] = useLocalStorage('showHelp', true);

    const handleHideMoveHelpChip = useCallback(() => {
        setShowHelp(false);
    }, [setShowHelp]);

    const { isMobile, isAndroid, isIPad } = useUserAgent(navigator.userAgent);
    const isTouch = isMobile || isAndroid || isIPad;
    const moveHelpChipKey = isTouch ? 'move-help-touch' : 'move-help';
    const moveHelpChipRendered = useMemo(() => (showHelp ? <StyledChip
        size="small"
        label={t(moveHelpChipKey)}
        onDelete={handleHideMoveHelpChip}
    /> : null), [handleHideMoveHelpChip, showHelp, moveHelpChipKey, t]);

    return (
        <StyledBox>
            <Grid container direction="row" justifyContent="space-between">
                <Grid item>
                    <Paper elevation={2}>
                        <Grid container direction="row" sx={{ padding: '2px' }}>
                            <ZoomTools {...props}/>
                            {/* <UndoRedo {...props}/> */}
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item>
                    <ElementTools {...props} t={t} />
                    {moveHelpChipRendered}
                </Grid>
                <Grid item>
                    <MiscTools {...props}/>
                </Grid>
            </Grid>
        </StyledBox>
    );
});

export default Tools;
